import { Box } from '@mui/material';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const RenderHTML = ({
  HTML,
  className = '',
  sx = {},
  onClick,
  id,
  innerRef,
  emptyHTML,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      containerRef.current = node;
      if (typeof innerRef === 'function') {
        innerRef(node);
      } else if (innerRef) {
        innerRef.current = node;
      }
    },
    [innerRef],
  );

  const html = useMemo(
    () =>
      HTML ||
      emptyHTML ||
      "<h1 style='text-align: center;'>" + t('EMPTY') + '</h1>',
    [HTML, emptyHTML, t],
  );

  useEffect(() => {
    var el = containerRef.current;
    if (!el) return;

    // Template scripts use document.getElementById etc., but several copies of
    // the same template can be mounted at once (side preview, final dialog,
    // paginated tabs) with identical element ids. Scope DOM lookups to this
    // page (header + content + footer share one StyledTextContainer), so each
    // preview's scripts only see and modify their own copy.
    const scopeRoot = el.closest('[data-template-page]') || el;
    const scopedDocument = new Proxy(document, {
      get(target, prop) {
        if (prop === 'getElementById')
          return (id) => scopeRoot.querySelector(`[id="${CSS.escape(id)}"]`);
        if (prop === 'querySelector')
          return (sel) => scopeRoot.querySelector(sel);
        if (prop === 'querySelectorAll')
          return (sel) => scopeRoot.querySelectorAll(sel);
        if (prop === 'getElementsByClassName')
          return (names) => scopeRoot.getElementsByClassName(names);
        if (prop === 'getElementsByTagName')
          return (name) => scopeRoot.getElementsByTagName(name);
        const value = Reflect.get(target, prop);
        return typeof value === 'function' ? value.bind(target) : value;
      },
      set(target, prop, value) {
        return Reflect.set(target, prop, value);
      },
    });

    var scripts = el.querySelectorAll('script');
    scripts.forEach(function (script) {
      var type = script.getAttribute('type');
      if (
        type &&
        type !== 'text/javascript' &&
        type !== 'application/javascript'
      )
        return;
      if (script.src) return;
      var content = script.textContent;
      if (!content || !content.trim()) return;
      try {
        new Function('document', content)(scopedDocument);
      } catch (e) {
        // ignore - script may contain template markup, not executable JS
      }
    });
  }, [html]);

  return (
    <Box
      ref={setRefs}
      id={id}
      onClick={onClick}
      className={className}
      sx={{
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default RenderHTML;
